/**
 * components/charts/PnlChart — realised P&L over time, in R. Per-day bars carry the daily
 * result (diverging: profit-green above the 0R baseline, loss-red below it) and a single
 * accent line traces the cumulative running total. Both series are the SAME unit (R) on
 * ONE axis — never a second y-scale — so the bars and the line are directly comparable.
 * The line is labelled at its final point (the net); the bars carry a per-day tooltip.
 */
import { useMemo, useRef, useState } from 'react';

import type { DayPnl } from '../../lib/analytics';

interface PnlChartProps {
  readonly days: readonly DayPnl[];
  readonly height?: number;
}

const VIEW_W = 720;
const PAD = { l: 40, r: 52, t: 16, b: 28 } as const;

export function PnlChart({ days, height = 260 }: PnlChartProps): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const geo = useMemo(() => {
    if (days.length === 0) return null;
    const plotW = VIEW_W - PAD.l - PAD.r;
    const plotH = height - PAD.t - PAD.b;
    const vals = days.flatMap((d) => [d.r, d.cumR, 0]);
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const padV = (hi - lo) * 0.1 || 1;
    const y0 = lo - padV;
    const y1 = hi + padV;
    const y = (v: number): number => PAD.t + (1 - (v - y0) / (y1 - y0 || 1)) * plotH;
    const slot = plotW / days.length;
    const gap = Math.min(6, slot * 0.24);
    const barW = Math.max(2, slot - gap);
    const cx = (i: number): number => PAD.l + i * slot + slot / 2;
    const zeroY = y(0);
    const line = days.map((d, i) => `${i === 0 ? 'M' : 'L'}${cx(i).toFixed(1)} ${y(d.cumR).toFixed(1)}`).join(' ');
    const ticks = [y1, (y1 + y0) / 2, y0];
    return { plotW, plotH, y, slot, gap, barW, cx, zeroY, line, ticks, y0, y1 };
  }, [days, height]);

  if (!geo) return <div className="state"><span className="muted">NO DATA YET</span></div>;

  const last = days[days.length - 1]!;
  const wrapW = wrapRef.current?.clientWidth ?? VIEW_W;
  const scale = wrapW / VIEW_W;
  const hovered = hover != null ? days[hover] : null;

  const onMove = (e: React.PointerEvent<SVGSVGElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * VIEW_W;
    const i = Math.floor((svgX - PAD.l) / geo.slot);
    setHover(i >= 0 && i < days.length ? i : null);
  };

  return (
    <div className="chart chart--pnl" ref={wrapRef} style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${VIEW_W} ${height}`} role="img" aria-label="Realised P&L per day and cumulative, in R" preserveAspectRatio="none" onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
        {geo.ticks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.l} x2={VIEW_W - PAD.r} y1={geo.y(t)} y2={geo.y(t)} stroke="var(--viz-grid)" strokeWidth={1} />
            <text x={PAD.l - 6} y={geo.y(t) + 3} fontSize={11} fill="var(--text-muted)" textAnchor="end" fontFamily="var(--font-mono)">{fmtR(t)}</text>
          </g>
        ))}

        {/* 0R baseline */}
        <line x1={PAD.l} x2={VIEW_W - PAD.r} y1={geo.zeroY} y2={geo.zeroY} stroke="var(--viz-axis)" strokeWidth={1} />

        {/* daily bars */}
        {days.map((d, i) => {
          const yTop = geo.y(Math.max(0, d.r));
          const yBot = geo.y(Math.min(0, d.r));
          const color = d.r >= 0 ? 'var(--profit)' : 'var(--loss)';
          return (
            <rect key={i} x={geo.cx(i) - geo.barW / 2} y={yTop} width={geo.barW} height={Math.max(1, yBot - yTop)} rx={2} fill={color} fillOpacity={hover === i ? 1 : 0.62}>
              <title>{`${d.day}: ${fmtR(d.r)}R on ${d.trades} trade${d.trades === 1 ? '' : 's'} · cum ${fmtR(d.cumR)}R`}</title>
            </rect>
          );
        })}

        {/* cumulative line */}
        <path d={geo.line} fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {days.map((d, i) => <circle key={i} cx={geo.cx(i)} cy={geo.y(d.cumR)} r={2.4} fill="var(--accent)" />)}

        {/* crosshair */}
        {hovered && <line x1={geo.cx(hover!)} x2={geo.cx(hover!)} y1={PAD.t} y2={PAD.t + geo.plotH} stroke="var(--viz-axis)" strokeWidth={1} strokeDasharray="3 3" />}

        {/* net label on the final cumulative point */}
        <circle cx={geo.cx(days.length - 1)} cy={geo.y(last.cumR)} r={4} fill="var(--accent)" stroke="var(--viz-surface)" strokeWidth={2} />
        <text x={geo.cx(days.length - 1) + 8} y={geo.y(last.cumR) + 3} fontSize={11} fill="var(--accent)" fontFamily="var(--font-mono)">{fmtR(last.cumR)}R</text>
      </svg>

      {hovered && (
        <div className="chart-tooltip" style={{ left: geo.cx(hover!) * scale, top: geo.y(hovered.cumR) * scale }}>
          <strong className="num">{fmtR(hovered.cumR)}R cum</strong>
          <span className="muted" style={{ marginLeft: 8 }}>{hovered.day} · {fmtR(hovered.r)}R</span>
        </div>
      )}

      <div className="chart__caption">
        <span className="chart__legend-swatch" style={{ background: 'var(--accent)' }} aria-hidden />
        <span>Cumulative R</span>
        <span className="chart__legend-swatch" style={{ background: 'var(--profit)', marginLeft: 12 }} aria-hidden />
        <span>Daily R (green/red by sign)</span>
      </div>
    </div>
  );
}

function fmtR(v: number): string {
  const s = Math.abs(v).toFixed(v % 1 === 0 ? 0 : 2);
  return `${v < 0 ? '−' : v > 0 ? '+' : ''}${s}`;
}
